import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { checkFormValidity } from 'src/utils/formFunctions';
import { useCheckPermission } from 'src/utils/checkPermission';
import ItemsTable from './ItemsTable';
import TopBlock from './TopBlock';
import getBackUrl from 'src/utils/getBackUrl';
import { editRows } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import NewItemBlock from './NewItemBlock';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { ConstantsCodeValueType } from 'src/types/constantsCodes';
import { TableColumnType } from 'src/types/table';
import { getColumns } from './functions';
import getCountryOptions from 'src/utils/countryOptions';
import { updateConstantDetailFetch } from 'src/resources/constantsCodes/reducer';
import Loading from 'src/components/Loading/intex';
import { FormErrorsType } from '../../types/global';

export type OnChangeType = (value: any, error: boolean, errorKey?: string) => void;

export interface EditConstantsCodeType extends Partial<ConstantsCodeType> {
    ID?: number;
    label?: string;
    value?: ConstantsCodeValueType;
}

const ConstantDetailContainer: FC = () => {
    const dispatch = useDispatch();

    const { constantKey } = useParams();
    if (!constantKey) {
        return null;
    }

    const itemsFormRef = useRef<HTMLFormElement>(null);

    const loading = useSelector((state: RootStoreType) => state.constantsCodes.loading);
    const allConstantsCodes = useSelector((state: RootStoreType) => state.constantsCodes.data);
    const constantsCodes = allConstantsCodes[constantKey];

    const locationHistory = useSelector((state: RootStoreType) => state.location.history);

    const [editData, setEditData] = useState<EditConstantsCodeType[]>();
    const [allColumns, setAllColumns] = useState<TableColumnType[]>([]);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [countryCodes, setCountryCodes] = useState<ConstantsCodeType[]>([]);

    const disableEdit = !editRows.includes(constantKey) || !useCheckPermission('CONSTATN.UPDATE');

    const backUrl = getBackUrl(locationHistory, '/admin/constants/');

    useEffect(() => {
        setCountryCodes(getCountryOptions());
    }, []);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('constantDetail.title', { constantKey }), backUrl }));
    }, [constantKey, backUrl]);

    useEffect(() => {
        if (constantsCodes) {
            setEditData(constantsCodes.map((item, index) => ({ ...item, ID: index + 1 })));
        }
    }, [constantsCodes]);

    useEffect(() => {
        if (constantKey) {
            setAllColumns(getColumns(constantKey));
        }
    }, [constantKey]);

    const onChange: OnChangeType = (value, error, errorKey) => {
        const newData = [...value];
        const newErrors = { ...editErrors };
        if (errorKey) {
            newErrors[errorKey] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(itemsFormRef);

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate('constantDetail.save'),
                content: translate('constantDetail.saveConfirm'),
                action: () => {
                    const formatData = editData?.map((item) => {
                        const newItem = { ...item, label: item.rawLabel };
                        delete newItem.ID;
                        return newItem;
                    });
                    dispatch(updateConstantDetailFetch({ constantKey, data: formatData }));
                },
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock disableEdit={disableEdit} handleSave={handleSave} />

            {loading && <Loading />}

            <FormBlock>
                <NewItemBlock
                    data={editData}
                    onChange={(value: any) => onChange(value, false)}
                    disableEdit={disableEdit}
                    allColumns={allColumns}
                    constantKey={constantKey}
                    allConstantsCodes={allConstantsCodes}
                    countryCodes={countryCodes}
                />
            </FormBlock>
            <form ref={itemsFormRef}>
                <ItemsTable
                    data={editData}
                    editErrors={editErrors}
                    onChange={onChange}
                    disableEdit={disableEdit}
                    allColumns={allColumns}
                    constantKey={constantKey}
                    allConstantsCodes={allConstantsCodes}
                    countryCodes={countryCodes}
                />
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default ConstantDetailContainer;
